import type {
  CardInstance,
  Composition,
  FittedAttachment,
  ForecastEntry,
  GamePhase,
  GridPos,
  NodeKind,
  RecipeDef,
  Rotation,
  ValveState,
} from "../protocol/snapshot";
import type { SimEvent, SpillCause } from "../protocol/events";
import type { CardDef, FacilityMitigation, PipeMaterial, PrecursorDef, SourcingDef, StageDef } from "./definitions";
import { NODE_DEFAULTS, PIPE_MAX_HP, VERMIN_LOST_TICK_PERIOD, VERMIN_LOST_TICK_PERIOD_MOUSETRAP, VERMIN_RISK_THRESHOLD } from "./definitions";

/**
 * `01-simulation-model.md` §3: workshop ambient, the temperature a feed emits
 * at when its tank sets no `feedTemperature` of its own (spec 0022).
 */
export const AMBIENT_TEMP_C = 20;
import { addComposition, contains } from "./composition";
import { createRng, shuffle, type Rng } from "./rng";

/**
 * Internal, MUTABLE engine state. Deliberately distinct from the readonly
 * GridSnapshot the renderer sees: the engine mutates this in place during a
 * tick, then projects an immutable snapshot out of it (see project.ts).
 */

export interface EnginePacket {
  id: string;
  composition: Composition;
  /** Integer °C, per 01-simulation-model.md §3. Set at birth by `newPacket`'s `temperature` option and moved only by a `thermalRegulator`'s `deltaTemp` (spec 0022) — otherwise unchanged by transit or rest. */
  temperature: number;
  /** Consecutive ticks spent in a node buffer without moving (`03-hazards.md` §2.5). */
  residenceTicks: number;
  /**
   * incubator only: the hold's own counter, distinct from `residenceTicks`
   * (which keeps running even across a hold's transform — spec 0025, T4). It
   * accrues in step 5 only while the packet's own hold recipe resolves
   * (`resolveHoldRecipeForPacket`) and its `minTemperature` gate, if any, is
   * met.
   */
  holdTicks: number;
  /**
   * incubator only, engine-internal: true once this visit's hold has fired
   * (transforming or not), false again the moment the packet moves. This is
   * what stops a completed hold from being re-evaluated against a DIFFERENT
   * matching hold recipe in the same node — without it a packet a vat
   * transforms would immediately also match the curing hold on its own
   * transformed species and sludge in place, rather than releasing (spec
   * 0025, Resolved Question 5).
   */
  hasCompletedHold: boolean;
  /** Carries sludge, dust, or unwanted side-product species (01-simulation-model.md §3). */
  isContaminated: boolean;
  /**
   * Engine-internal fire-once guard for the residence-sludge hazard
   * (`03-hazards.md` §2.5), kept separate from `isContaminated` so a packet
   * that is already contaminated at birth (a Budget precursor) still sludges
   * exactly once after `SLUDGE_RESIDENCE_TICKS` — `!isContaminated` would
   * never fire for it.
   */
  hasResidenceSludged: boolean;
  /**
   * Set the moment a packet moves, cleared at the top of each tick. This is
   * what enforces the documented 1 unit/tick throughput: without it a packet
   * delivered into a node early in the movement step would be pushed straight
   * out again later in the same step, crossing two hops in one tick (and
   * never resting in a catalyst bed long enough to poison it).
   */
  movedThisTick: boolean;
  /**
   * True while this packet is the OUTPUT of a reaction at the node holding it,
   * cleared the moment it is delivered somewhere new.
   *
   * Without it, `isAwaitingReaction` traps any product whose species also
   * appear among a recipe's inputs: a purification step (NaCl + H2O -> NaCl)
   * would hold its own dry product forever waiting for water, and a two-step
   * synthesis would hold the intermediate at the node that just made it.
   * "Raw reagent waits for its partner; product moves on" is the actual rule,
   * and this is what makes the distinction expressible.
   */
  isProduct: boolean;
  /**
   * Engine-internal only (no `PacketState` projection): which fraction a
   * phase converter produced this packet as, so `pushFromNode` (movement.ts)
   * can route it down the matching `phaseRole` edge (spec 0003). Set only by
   * `phase.ts` at the instant of conversion; cleared to `null` the moment the
   * packet is delivered anywhere new, exactly like `isProduct` — it only ever
   * needs to steer the packet out of the converter that just made it.
   */
  phaseRole: "vapour" | "liquid" | null;
  /**
   * Engine-internal only (no `PacketState` projection): which fraction a
   * separator produced this packet as, so `pushFromNode` (movement.ts) can
   * route it down the matching `separationRole` edge (spec 0023), mirroring
   * `phaseRole` exactly. Set only by `separation.ts` at the instant of
   * partition; cleared to `null` the moment the packet is delivered anywhere
   * new — it only ever needs to steer the packet out of the separator that
   * just produced it.
   */
  separationRole: "main" | "waste" | null;
}

export interface EngineNode {
  id: string;
  kind: NodeKind;
  pos: GridPos;
  rotation: Rotation;
  buffer: EnginePacket[];
  bufferCapacity: number;

  heatCounter: number;
  criticalTemp: number;
  isOverheatWarning: boolean;
  /** Consecutive ticks spent at or above criticalTemp without being cooled. */
  overheatTicks: number;

  catalystHealth: number;
  isDestroyed: boolean;

  /**
   * Remaining guard-bed / polishing-filter charges. Attachments that raise a
   * stat write it directly, but this one is spent as it works, so it needs
   * somewhere to count down (`03-hazards.md` §2.4).
   */
  guardCharges: number;

  attachments: FittedAttachment[];

  /** inputTank only: what it emits each tick. */
  emits: Composition | null;

  /**
   * inputTank only: the active contract, `null` everywhere else. For a
   * sourced tank (one with `sourcingOptions` below) this is `null` until
   * `setPrecursor` picks a tier — nothing pre-charged, nothing emitted
   * (spec 0027's "no default contract"). For an unsourced tank it is always
   * `{ tier: "premium" }`, free and uncapped — today's behaviour.
   */
  precursor: PrecursorDef | null;

  /**
   * inputTank with authored `sourcing` only: the tank's own contract menu,
   * mirroring `emits`'s "what def says, once" role. `null` for every other
   * kind and for an unsourced inputTank (spec 0027).
   */
  sourcingOptions: SourcingDef | null;

  /**
   * inputTank only: units left before a capped Premium contract runs dry.
   * `null` until a capped Premium contract is chosen — stays `null` forever
   * for Budget, an uncapped Premium, or a tank with no `sourcing` at all
   * (spec 0027).
   */
  supplyRemaining: number | null;

  /** thermalRegulator only: integer °C applied to its buffer each tick (spec 0022). Null everywhere else, including an unconfigured regulator. */
  deltaTemp: number | null;

  /** inputTank only: emission temperature in integer °C (spec 0022). Null everywhere else, meaning AMBIENT_TEMP_C. */
  feedTemperature: number | null;

  /** flowMover only: round-robin cursor, so routing is deterministic (no RNG). */
  splitCursor: number;

  /**
   * flowMover only (spec 0024): `+1` buffer depth this mover may push past
   * downstream (§4.7). `0` everywhere else, including an unconfigured mover —
   * mirrors `NodeDef.pumpOverride`/`CardDef.deploysNodeConfig.pumpOverride`.
   */
  pumpOverride: number;

  /**
   * flowMover only (spec 0024): the player-owned routing setting. `null` for
   * every other kind and for a mover the stage left on automatic
   * round-robin. Mirrors `NodeState.valveState`.
   */
  valveState: ValveState | null;

  /**
   * Sludged nodes run at 0.5 units/tick, i.e. they act every other tick.
   * This counts ticks so the halving is exact rather than probabilistic.
   */
  halfSpeedToggle: boolean;

  /** Spec 0035: Low Fire's per-turn cap on `k`; `null` = uncapped. Cleared on entry to `draw`. */
  maxMultipleThisTurn: number | null;

  /**
   * This tick's answer to "is this sludged node active?", resolved once by
   * `resolveSludgeGate` at the top of tick.ts's step 2 loop and read — not
   * recomputed — by `processReactions`, `processPhaseConversion` and
   * `pushFromNode`, so all three run on the same every-other-tick cadence
   * instead of `halfSpeedToggle` flipping twice in one tick
   * (`03-hazards.md` §2.5). Always `true` for a node with no sludged packet
   * in its buffer — the gate is additive, never a replacement for a node's
   * other early-outs (destroyed, vermin, power freeze).
   */
  sludgeActiveThisTick: boolean;

  /**
   * 03-hazards.md §3.3 (spec 0033): `null` while clean. `ticksInfested` counts
   * ticks since arrival (incremented by `resolveInfestationGate`, step 2);
   * `heldPacketThisTurn` tracks whether the node has held >=1 packet at any
   * point in the current turn, for the starve-out check on `status` entry
   * (`phases.ts`). Only ever non-null on an infestable kind.
   */
  infestation: { sinceTurn: number; ticksInfested: number; heldPacketThisTurn: boolean } | null;

  /**
   * This tick's answer to "is this infested node active?", resolved once by
   * `resolveInfestationGate` at the top of tick.ts's step 2 loop, alongside
   * `resolveSludgeGate` — read, not recomputed, by every pass §3.3 throttles.
   * Always `true` for a node that isn't infested.
   */
  verminActiveThisTick: boolean;

  /**
   * Set only by `setup.ts`'s `installHardware` (spec 0017); `null` for every
   * stage-authored node, including one placed by the legacy `playCard`-driven
   * `applyHardware` in `cards.ts`. Lets `dismantleHardware` tell a
   * player-bought node from the stage's own rig, and carries the ids its
   * `hardware.dismantled` event needs to report.
   */
  installedHardware: InstalledHardware | null;

  /**
   * Spec 0031: when non-null, this node runs ONLY these recipe ids (still
   * filtered by `RecipeDef.nodeKinds`) — see `recipesForNode`. `null` means
   * unbound: every recipe whose `nodeKinds` include this kind, today's
   * behaviour. Copied from `NodeDef.recipeIds` at creation and from
   * `CardDef.deploysNodeConfig.recipeIds` for a deployed node.
   */
  recipeIds: readonly string[] | null;
}

/** A packet part-way along a pipe. `pathIndex` is its current waypoint. */
export interface EngineTransit {
  packet: EnginePacket;
  pathIndex: number;
}

/**
 * 03-hazards.md §3.1 (spec 0005): a grid tile the engine has marked
 * contaminated. The engine has no other position-keyed state today — nodes
 * carry a `pos`, and `findNodeAt` is the only position lookup — so this is a
 * sparse array (not a dense cols*rows map), plainly serializable like
 * `pollutionSources`.
 */
export interface EngineTile {
  pos: GridPos;
  cause: SpillCause;
  sourceId: string;
  sinceTurn: number;
}

/**
 * What Setup's `installHardware` records on the node or pipe it bought
 * (spec 0017): the ids `hardware.dismantled` reports, and the price paid, so
 * a same-setup dismantle refunds exactly that (`02-cards-and-economy.md`'s
 * "Purchased in Current Setup Phase: 100% Credit Refund").
 */
export interface InstalledHardware {
  cardId: string;
  instanceId: string;
  costCredits: number;
}

export interface EngineEdge {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  path: GridPos[];
  material: PipeMaterial;
  hp: number;
  maxHp: number;
  pressureCounter: number;
  isOverpressured: boolean;
  isLeaking: boolean;
  isBurst: boolean;
  /** Packets in flight, like a conveyor: at most one per waypoint. */
  transit: EngineTransit[];
  /** phaseConverter outputs only: which fraction this edge carries (spec 0003). */
  phaseRole: "vapour" | "liquid" | null;
  /** separator outputs only: which fraction this edge carries (spec 0023), mirrors `phaseRole`. */
  separationRole: "main" | "waste" | null;
  /**
   * Set only when `setup.ts`'s `installHardware` bought this pipe (spec 0017);
   * `null` for stage-authored pipes and ones laid by a Play-phase card. The
   * edge counterpart of `EngineNode.installedHardware`.
   */
  installedHardware: InstalledHardware | null;
}

export interface EngineState {
  stage: StageDef;
  rng: Rng;
  turn: number;
  phase: GamePhase;
  tickInTurn: number;

  /**
   * G-D9 (spec 0028): within a turn, `advancePhase`'s Play entry resets this
   * to `stage.energyBudgetKw`, then a held penalty (Thermal Shock,
   * `powerPenaltyKw`) or a scripted/held blackout (`isBlackoutPending`,
   * `powerDrain`) lowers it, then an addition — an Overtime Shift played mid-
   * turn, or Emergency Power Cell firing on the same entry — raises it; the
   * result floors at 0. This is the field's one documented owner: don't
   * write it from a fourth site without updating this comment and
   * `02-cards-and-economy.md`'s mirror of the same rule.
   */
  energyBudgetKw: number;
  energySpentKw: number;

  /** Phase 1's budget and spend, in ₵ (spec 0017). Stage-scoped, like `energyBudgetKw`; the engine never learns where the ₵ came from. */
  setupBudgetCredits: number;
  setupSpentCredits: number;

  nodes: EngineNode[];
  edges: EngineEdge[];

  collected: Composition;

  /**
   * Scoring tallies (`02-cards-and-economy.md` §5). Purity is accumulated unit-weighted as an
   * integer sum and only divided at projection time, so no rounding creeps in
   * over a long run.
   */
  scoredUnits: number;
  purityUnitsWeighted: number;
  wastedUnits: number;

  /**
   * Spec 0023: total units collected at every wasteBuyer, tallied separately
   * from `scoredUnits` — a waste delivery counts toward nothing the objective
   * checks. Spec 0026 reports this as `RunTallies.valorizedUnits`; the engine
   * itself still prices nothing — `@reagent/meta` turns it into Credits.
   */
  valorizedUnits: number;

  /**
   * Spec 0032: total units collected at every `vent`, tallied separately from
   * `valorizedUnits` and `wastedUnits` — a vented delivery is neither sold nor
   * dumped. `RunTallies.ventedUnits` projects this; `@reagent/meta` prices it
   * as `ventFine`, the same split `valorizedUnits` has with valorization credits.
   */
  ventedUnits: number;

  /**
   * Which pieces of neglect have already cost a hazard card, so a single
   * leaking pipe pollutes the deck once rather than every tick it leaks.
   * Keyed `<reason>:<id>`; an array keeps state plainly serializable.
   */
  pollutionSources: string[];

  hand: CardInstance[];
  drawPile: CardInstance[];
  discardPile: CardInstance[];

  isRunComplete: boolean;
  isRunFailed: boolean;
  failureReason: "explosion" | "burst" | "leak" | "quotaMissed" | null;

  /** Monotonic id source — never Math.random, so ids replay identically. */
  nextPacketSeq: number;
  nextNodeSeq: number;
  nextEdgeSeq: number;
  /**
   * Monotonic id source for `CardInstance`s minted mid-stage (spec 0016) —
   * today only `pollution.ts`'s `pollute()`. Prefixed `h` ("hazard-injected")
   * so these ids can never collide with content's `d`-prefixed starting-deck
   * ids or @reagent/meta's `r`-prefixed ones once mixed into one hand.
   */
  nextInstanceSeq: number;

  /**
   * Clogged Line's locked tile (`03-hazards.md` §4), keyed by the holding
   * card's own `instanceId` rather than derived from hand position or a
   * shared pollution-source list — this is what closes `GAPS.md` G-F7:
   * destroying the sludge source behind one held copy can never shift which
   * tile a different, later-held copy locks. Populated once, at mint time, by
   * `pollution.ts`'s `pollute()`; not a field on the public `CardInstance`
   * (kept to exactly five fields), the same way `EnginePacket` carries
   * engine-only bookkeeping the projected `PacketState` never sees.
   */
  cloggedLineLockedTiles: Record<string, GridPos>;

  /** 03-hazards.md §3.1 (spec 0005). Marked once per tile; see `isTileContaminated`. */
  contaminatedTiles: EngineTile[];
  /** 03-hazards.md §3.3 (spec 0005): the facility-wide pest counter. */
  verminRisk: number;

  /**
   * 03-hazards.md §3.2 (spec 0006): ticks remaining on a scripted power
   * surge's freeze of `flowMover` nodes, `coolingJacket` attachments, and
   * `thermalRegulator` nodes. Decremented at the end of tick step 1.
   */
  powerFreezeTicks: number;
  /**
   * §3.2 (spec 0006, Open Question 2): set by a scripted surge, consumed on
   * entry to the *next* Play phase — that's when `energySpentKw` is written
   * up to `energyBudgetKw`, not on the tick the surge fires.
   */
  isBlackoutPending: boolean;
  /** §3.4 (spec 0006, Open Question 4): standing modifier, trigger turn to end of stage. */
  isHeatwaveActive: boolean;
  /**
   * §3.2/§3.4 (spec 0006): `<turn>:<kind>` keys already fired, so a scripted
   * event dispatches exactly once ever, mirroring `pollutionSources`' shape.
   */
  firedScriptedEvents: string[];

  /**
   * Line Leak (spec 0007): the draw-size penalty a hazard card left unplayed
   * at end of Play charges on the *next* `drawHand`. Consumed and reset to 0
   * by that draw, so the penalty is exactly one turn, never compounding on
   * its own.
   */
  handSizePenaltyNextTurn: number;
  /**
   * Structural Shear / Catalyst Deactivation and any other per-turn-while-held
   * penalty (spec 0007): `<cardId>:<turn>` keys, so a per-turn charge lands
   * once per turn per copy — the same shape as `pollutionSources`.
   */
  chargedPenalties: string[];

  /**
   * Spec 0018: every hazard threshold a dry run of the coming turn would
   * cross if the player played nothing more. Recomputed by `computeForecast`
   * (forecast.ts) on entering Play and after each accepted command — never
   * during projection, so `getSnapshot()` stays a pure read. Empty outside
   * Play.
   */
  forecast: ForecastEntry[];

  /**
   * Spec 0028: Phase 1 facility mitigations bought this stage, keyed by the
   * card that sold them. `installHardware` (setup.ts) refuses a second
   * install of the same kind, so this holds at most one entry per
   * `FacilityMitigationKind`. `spent` flips true the instant a one-shot
   * (Hazmat Absorbent, Emergency Power Cell) fires; a standing mitigation
   * (Surge Protector, Facility Mousetrap, HVAC Chillers, Seismic Isolation
   * Mounts) never spends.
   */
  installedFacilityMitigations: { cardId: string; mitigation: FacilityMitigation; spent: boolean }[];

  /**
   * Spec 0028: Facility Mousetrap halves §3.3's verminRisk accrual by
   * skipping every second occurrence rather than flooring a division — a
   * stage-scoped parity counter incremented once per accrual event
   * (mitigated or not), so the skip pattern is the same regardless of which
   * call site (residence in `accumulateVermin`, or an unsealed `wasteBuyer`
   * in `dispatchScriptedEvents`) triggered it.
   */
  verminAccrualCount: number;

  /** Spec 0067: spill-free loaded moves spent this turn; reset on Play entry. */
  spillFreeMovesUsedThisTurn: number;
}

export function createInitialState(stage: StageDef, seed: number): EngineState {
  const rng = createRng(seed);

  const nodes: EngineNode[] = stage.nodes.map((def) => ({
    id: def.id,
    kind: def.kind,
    pos: def.pos,
    rotation: def.rotation ?? NODE_DEFAULTS.rotation,
    buffer: [],
    bufferCapacity: def.bufferCapacity ?? NODE_DEFAULTS.bufferCapacity,
    heatCounter: 0,
    criticalTemp: def.criticalTemp ?? NODE_DEFAULTS.criticalTemp,
    isOverheatWarning: false,
    overheatTicks: 0,
    catalystHealth: 100,
    isDestroyed: false,
    guardCharges: 0,
    attachments: [],
    emits: def.emits ?? null,
    // No default contract (spec 0027, Constraints): a sourced tank opens
    // Setup with nothing chosen and nothing pre-charged; an unsourced one
    // keeps today's behaviour of a free, uncapped Premium feed.
    precursor: def.kind === "inputTank" && !def.sourcing ? { tier: "premium" } : null,
    sourcingOptions: def.kind === "inputTank" ? (def.sourcing ?? null) : null,
    supplyRemaining: null,
    deltaTemp: def.deltaTemp ?? null,
    recipeIds: def.recipeIds ?? null,
    feedTemperature: def.feedTemperature ?? null,
    splitCursor: 0,
    pumpOverride: def.pumpOverride ?? 0,
    valveState: null,
    halfSpeedToggle: false,
    maxMultipleThisTurn: null,
    sludgeActiveThisTick: true,
    infestation: null,
    verminActiveThisTick: true,
    installedHardware: null,
  }));

  const edges: EngineEdge[] = stage.edges.map((def) => ({
    id: def.id,
    fromNodeId: def.fromNodeId,
    toNodeId: def.toNodeId,
    path: [...def.path],
    material: def.material ?? "glass",
    hp: PIPE_MAX_HP,
    maxHp: PIPE_MAX_HP,
    pressureCounter: 0,
    isOverpressured: false,
    isLeaking: false,
    isBurst: false,
    transit: [],
    phaseRole: def.phaseRole ?? null,
    separationRole: def.separationRole ?? null,
    installedHardware: null,
  }));

  const state: EngineState = {
    stage,
    rng,
    turn: 0,
    // Phase 1: Workshop Setup (spec 0017) — frozen time before turn 1, with
    // nothing dealt yet. The opening hand is dealt naturally the first time
    // advancePhase carries the phase machine setup -> draw (phases.ts).
    phase: "setup",
    tickInTurn: 0,
    energyBudgetKw: stage.energyBudgetKw,
    energySpentKw: 0,
    setupBudgetCredits: stage.setupBudgetCredits ?? 0,
    setupSpentCredits: 0,
    nodes,
    edges,
    collected: {},
    scoredUnits: 0,
    purityUnitsWeighted: 0,
    wastedUnits: 0,
    valorizedUnits: 0,
    ventedUnits: 0,
    pollutionSources: [],
    hand: [],
    drawPile: shuffle([...stage.startingDeck], rng),
    discardPile: [],
    isRunComplete: false,
    isRunFailed: false,
    failureReason: null,
    nextPacketSeq: 1,
    nextNodeSeq: 1,
    nextEdgeSeq: 1,
    nextInstanceSeq: 1,
    cloggedLineLockedTiles: {},
    contaminatedTiles: [],
    verminRisk: 0,
    powerFreezeTicks: 0,
    isBlackoutPending: false,
    isHeatwaveActive: false,
    firedScriptedEvents: [],
    handSizePenaltyNextTurn: 0,
    chargedPenalties: [],
    forecast: [],
    installedFacilityMitigations: [],
    verminAccrualCount: 0,
    spillFreeMovesUsedThisTurn: 0,
  };

  return state;
}

/**
 * Draws up to handSize cards, reshuffling the discard pile back in when the
 * draw pile runs dry. Uses the seeded RNG, so a hand is reproducible.
 *
 * Lives here rather than in phases.ts because createInitialState needs it and
 * phases.ts already depends on this module.
 *
 * A held Chemical Hazard Spill (spec 0007) is the one card that survives this
 * call: it stays in hand across the boundary rather than going to discard,
 * and it still eats a slot out of the new hand's draw target. Line Leak's
 * draw-size penalty is consumed here too, win or lose — the field resets to 0
 * whether or not it was positive, so it can never compound on its own.
 */
export function drawHand(state: EngineState, cards: Record<string, CardDef>): void {
  const keep: CardInstance[] = [];
  const toDiscard: CardInstance[] = [];
  for (const instance of state.hand) {
    const card = cards[instance.cardId];
    const isLocked = card?.category === "hazard" && card.hazard?.kind === "handSlotLock";
    (isLocked ? keep : toDiscard).push(instance);
  }
  state.discardPile.push(...toDiscard);
  state.hand = keep;

  const penalty = state.handSizePenaltyNextTurn;
  state.handSizePenaltyNextTurn = 0;
  const target = Math.max(0, state.stage.handSize - penalty - keep.length);

  for (let i = 0; i < target; i++) {
    if (state.drawPile.length === 0) {
      if (state.discardPile.length === 0) break;
      state.drawPile = shuffle(state.discardPile, state.rng);
      state.discardPile = [];
    }
    const instance = state.drawPile.shift();
    if (!instance) break;
    state.hand.push(instance);
  }
}

export function findNode(state: EngineState, nodeId: string): EngineNode | undefined {
  return state.nodes.find((n) => n.id === nodeId);
}

export function findNodeAt(state: EngineState, pos: GridPos): EngineNode | undefined {
  return state.nodes.find((n) => !n.isDestroyed && n.pos.x === pos.x && n.pos.y === pos.y);
}

/** 03-hazards.md §3.1 (spec 0005): true once any tile at this exact position has been marked. */
export function isTileContaminated(state: EngineState, pos: GridPos): boolean {
  return state.contaminatedTiles.some((t) => t.pos.x === pos.x && t.pos.y === pos.y);
}

export function findEdge(state: EngineState, edgeId: string): EngineEdge | undefined {
  return state.edges.find((e) => e.id === edgeId);
}

/** Live edges leaving a node, in stable id order so routing is deterministic. */
export function outgoingEdges(state: EngineState, nodeId: string): EngineEdge[] {
  return state.edges
    .filter((e) => e.fromNodeId === nodeId && !e.isBurst)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export function edgesTouching(state: EngineState, nodeId: string): EngineEdge[] {
  return state.edges.filter((e) => e.fromNodeId === nodeId || e.toNodeId === nodeId);
}

export function newPacket(
  state: EngineState,
  composition: Composition,
  options?: {
    readonly isProduct?: boolean;
    readonly isContaminated?: boolean;
    readonly temperature?: number;
    readonly phaseRole?: "vapour" | "liquid";
    readonly separationRole?: "main" | "waste";
  },
): EnginePacket {
  return {
    id: `p${state.nextPacketSeq++}`,
    composition,
    temperature: options?.temperature ?? 0,
    residenceTicks: 0,
    holdTicks: 0,
    hasCompletedHold: false,
    isContaminated: options?.isContaminated ?? false,
    hasResidenceSludged: false,
    movedThisTick: false,
    isProduct: options?.isProduct ?? false,
    phaseRole: options?.phaseRole ?? null,
    separationRole: options?.separationRole ?? null,
  };
}

/**
 * Mints a `CardInstance` for a card the engine hands out mid-stage rather
 * than dealing from the deck — `pollution.ts`'s `pollute()`, and now
 * `setup.ts`'s `installHardware` (spec 0017), which mints one for the
 * hardware it places so a bought node binds to an instance the same way a
 * played one does. See `nextInstanceSeq`'s doc comment for why the `h`
 * prefix is disjoint from every other minter's own prefix.
 *
 * `boundNodeId` defaults to `null` — `pollute()` always mints unbound (spec
 * 0020 criterion 6: a hazard card belongs to the player, not the hardware
 * that provoked it) — and `setup.ts`'s injection path is the one caller that
 * passes a node id, so a purged instance is addressable by it.
 */
export function mintCardInstance(
  state: EngineState,
  cardId: string,
  boundNodeId: string | null = null,
): CardInstance {
  return {
    instanceId: `h${state.nextInstanceSeq++}`,
    cardId,
    acquiredStageIndex: 0, // provisional: the engine has no run stage index, so meta's finishStage re-stamps it with the id
    boundNodeId,
    isContaminated: false,
  };
}

/**
 * Spec 0028: the installed facility mitigation of this kind, if any — the
 * single reader `installedFacilityMitigations` is meant to have, so its call
 * sites (facility.ts's surge/heatwave/seismic handling, hazards.ts's
 * contamination and vermin-disable-duration handling, phases.ts's kW
 * ordering, and setup.ts's duplicate-install refusal) can't drift on how a
 * mitigation is found.
 */
export function facilityMitigationOf<K extends FacilityMitigation["kind"]>(
  state: EngineState,
  kind: K,
): Extract<FacilityMitigation, { kind: K }> | undefined {
  const found = state.installedFacilityMitigations.find((m) => m.mitigation.kind === kind);
  return found?.mitigation as Extract<FacilityMitigation, { kind: K }> | undefined;
}

/** Spec 0028: marks a one-shot mitigation fired. No-op if not installed or already spent. */
export function spendFacilityMitigation(state: EngineState, kind: FacilityMitigation["kind"]): void {
  const entry = state.installedFacilityMitigations.find((m) => m.mitigation.kind === kind);
  if (entry) entry.spent = true;
}

/**
 * 03-hazards.md §3.3 (spec 0033): the lost-tick period an infestation runs
 * at right now — 1 in 2, or 1 in 4 under Facility Mousetrap. Derived live off
 * `installedFacilityMitigations` rather than stored per-infestation: a
 * mitigation is bought once in Setup and never changes mid-stage (spec 0028),
 * so recomputing here is exactly equivalent to freezing the value at the
 * infestation tick, and it is what `resolveInfestationGate`, `resolveFailures`'s
 * §3.3 branch (its event payload) and `projectNode` all read, so the three
 * can't drift on which one a node is actually running.
 */
export function verminLostTickPeriodOf(state: EngineState): number {
  return facilityMitigationOf(state, "pestControl") !== undefined
    ? VERMIN_LOST_TICK_PERIOD_MOUSETRAP
    : VERMIN_LOST_TICK_PERIOD;
}

/**
 * Spec 0028: the single writer of `state.verminRisk`, so §3.3's two accrual
 * sites — `accumulateVermin`'s residence pass (hazards.ts) and the unsealed
 * `wasteBuyer` per-turn accrual (`dispatchScriptedEvents`, facility.ts) —
 * can't drift on how Facility Mousetrap halves the count. The parity counter
 * advances on every call regardless of whether Mousetrap is installed, so
 * the skip pattern the two sites see is identical; the first call of the
 * stage always raises `verminRisk` (odd counts fire, even counts are the
 * skipped side) — arbitrary but fixed here, not left to whichever call site
 * happens to run first.
 */
export function accrueVerminRisk(state: EngineState, sourceId: string, events: SimEvent[]): void {
  state.verminAccrualCount += 1;
  const halved = facilityMitigationOf(state, "pestControl") !== undefined;
  if (halved && state.verminAccrualCount % 2 === 0) return;

  state.verminRisk += 1;
  events.push({
    type: "facility.verminRisk",
    verminRisk: state.verminRisk,
    threshold: VERMIN_RISK_THRESHOLD,
    sourceId,
  });
}

/**
 * Total heat shed per tick by this node's attachments (`03-hazards.md` §2.1).
 * Contributes nothing while a scripted power surge's freeze is in force
 * (03-hazards.md §3.2, spec 0006, Open Question 3) — a chiller draws power
 * too.
 */
export function heatShedOf(state: EngineState, node: EngineNode): number {
  if (state.powerFreezeTicks > 0) return 0;

  let shed = 0;
  for (const { effect } of node.attachments) {
    if (effect.kind === "coolingJacket") shed += effect.heatShedPerTick;
  }
  return shed;
}

/**
 * `03-hazards.md` §2.5: a sludged node acts at 0.5 units/tick — reaction,
 * phase conversion and push alike, not just the push. Call exactly once per
 * node per tick, from tick.ts's step 2 loop before `processReactions` runs,
 * so `halfSpeedToggle` flips once and every gated pass this tick agrees on
 * the same answer. A node with no sludged packet in its buffer is never
 * throttled: the toggle only moves while sludge is present, and the gate
 * reads `true` in between.
 *
 * Spec 0033 (owner decision 11, one debuff per node): while a node is
 * infested, sludge stops throttling it — `sludgeActiveThisTick` is pinned
 * `true` and `halfSpeedToggle` is left untouched, so the toggle resumes
 * exactly where it left off once the infestation clears. A sludged-packet
 * node is never chosen as an infestation candidate (`resolveFailures`), so
 * this branch only matters for a packet that sludges INSIDE an already-
 * infested node (remaining question 2).
 */
export function resolveSludgeGate(node: EngineNode): boolean {
  if (node.infestation !== null) {
    node.sludgeActiveThisTick = true;
    return node.sludgeActiveThisTick;
  }

  if (node.buffer.some((p) => p.hasResidenceSludged)) {
    node.halfSpeedToggle = !node.halfSpeedToggle;
    node.sludgeActiveThisTick = node.halfSpeedToggle;
  } else {
    node.sludgeActiveThisTick = true;
  }
  return node.sludgeActiveThisTick;
}

/**
 * `03-hazards.md` §3.3 (spec 0033): an infested node works only on alternate
 * ticks — `ticksInfested` counts every tick spent infested, and the node is
 * active iff it is NOT the last tick of the period (`lostTickPeriod`, 2 or 4
 * under Facility Mousetrap). Call exactly once per node per tick, from
 * tick.ts's step 2 loop alongside `resolveSludgeGate`, and read — not
 * recomputed — by every pass §3.3 throttles.
 */
export function resolveInfestationGate(state: EngineState, node: EngineNode): boolean {
  if (node.infestation !== null) {
    node.infestation.ticksInfested += 1;
    node.verminActiveThisTick = node.infestation.ticksInfested % verminLostTickPeriodOf(state) !== 0;
  } else {
    node.verminActiveThisTick = true;
  }
  return node.verminActiveThisTick;
}

/** Extra pressure a burst disk buys the pipe before it lets go (`03-hazards.md` §2.2). */
export function pressureToleranceOf(node: EngineNode): number {
  let tolerance = 0;
  for (const { effect } of node.attachments) {
    if (effect.kind === "burstDisk") tolerance += effect.extraPressureTolerance;
  }
  return tolerance;
}

/**
 * A recipe carrying `boilingPoint`, `holdDuration` or `primarySpecies`
 * describes how a stream behaves rather than a transformation to fire —
 * `reactions.ts`'s `recipesFor` excludes it from the normal reaction-firing
 * pass with this same predicate, so the two files can't drift on what counts
 * as one (spec 0003, revision 2026-09-14; `primarySpecies` added by spec 0023).
 */
export function isPropertyRecipe(recipe: RecipeDef): boolean {
  return (
    recipe.boilingPoint !== undefined || recipe.holdDuration !== undefined || recipe.primarySpecies !== undefined
  );
}

/**
 * Spec 0031: the recipes this node may select from — every recipe whose
 * `nodeKinds` include this node's kind, narrowed to `node.recipeIds` when
 * bound, in stable id order. THE resolver every kind-only recipe selection
 * routes through (`resolvePropertyRecipe` and `resolveHoldRecipeForPacket`
 * below; `reactions.ts`'s `recipesFor` and `movement.ts`'s
 * `isAwaitingReaction` after T3). An unbound node (`recipeIds === null`)
 * still sees every kind-matching recipe, including one bound to another node
 * — exclusivity is the author's job, not this resolver's.
 */
export function recipesForNode(state: EngineState, node: EngineNode): RecipeDef[] {
  return state.stage.recipes
    .filter((r) => r.nodeKinds.includes(node.kind) && (node.recipeIds === null || node.recipeIds.includes(r.id)))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * Resolves the property recipe governing this node's buffer, the same way
 * `processReactions` resolves a firing recipe: pool the whole buffer, take
 * the first property recipe (in stable id order) whose `nodeKinds` include
 * this node's kind and whose `inputs` the pool contains. No match ⇒
 * `undefined`, and the node is inert this tick.
 */
export function resolvePropertyRecipe(state: EngineState, node: EngineNode): RecipeDef | undefined {
  const pooled = node.buffer.reduce((acc, p) => addComposition(acc, p.composition), {} as Composition);

  return recipesForNode(state, node)
    .filter(isPropertyRecipe)
    .find((r) => contains(pooled, r.inputs));
}

/**
 * phaseConverter only: °C at or above which volatiles flash off (spec 0003).
 * Resolved off the node's current buffer against the stage's property
 * recipes, in the shape of `heatShedOf`/`pressureToleranceOf` — no match
 * leaves the node inert this tick.
 */
export function phaseThresholdOf(state: EngineState, node: EngineNode): number | null {
  return resolvePropertyRecipe(state, node)?.boilingPoint ?? null;
}

/** PTFE lining makes a pipe immune to corrosion, as does hastelloy. */
export function resistsCorrosion(edge: EngineEdge): boolean {
  return edge.material === "ptfeLined" || edge.material === "hastelloy";
}

/** incubator only: configured hold duration a packet must sit through before release (spec 0003). */
export function holdTicksOf(state: EngineState, node: EngineNode): number {
  return resolvePropertyRecipe(state, node)?.holdDuration ?? 0;
}

/**
 * incubator only: the holdDuration recipe governing THIS packet, resolved
 * against its own composition — not the node's pooled buffer, unlike
 * `resolvePropertyRecipe`. Two incubators of the same kind must be able to
 * run different holds without one packet's presence deciding another
 * packet's fate (spec 0025). No match ⇒ undefined.
 */
export function resolveHoldRecipeForPacket(state: EngineState, node: EngineNode, packet: EnginePacket): RecipeDef | undefined {
  return recipesForNode(state, node).filter(
    (r) => r.holdDuration !== undefined && contains(packet.composition, r.inputs),
  )[0];
}
